/**
 * Registration-number prompt shown when someone becomes an employer.
 *
 * Until an account is attached to a company it has nothing to show under "Манай
 * компани" and nothing to post ads under, so this asks for the number at the moment
 * the role is chosen rather than leaving it to surface halfway through the posting
 * wizard. Same lookup and same find-or-create call the wizard uses, so an employer who
 * completes it here never sees the question again.
 *
 * Dismissible on purpose where the role was merely switched: the switch has already
 * happened, and forcing a company on someone still deciding is worse than letting them
 * come back to it.
 *
 * Two ways out, and callers must handle both: `onClose` when the person backs out, and
 * `onResolved(company)` when the company is registered — then the caller closes the dialog
 * itself. Never both.
 *
 * `required` is the other case — the posting wizard, where an ad cannot be filed without
 * a company. There the X and the backdrop are gone and the only way past is to register
 * or to leave, because a wizard you are allowed to fill in but never submit is worse
 * than being told the requirement up front.
 *
 * The state registry is the gate in both modes, not a convenience: only a company it
 * actually names can be saved. There used to be a typed-name fallback for a number it did
 * not know and for a registry that did not answer, which meant any seven digits under any
 * name created a company — the number was collected but never checked.
 */

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiX, FiCheckCircle } from "react-icons/fi";

import { previewCompanyRegistration, resolveCompany } from "../api/services/employerJobs";
import { lookupCompanyByRegNo } from "../utils/companyLookup";
import { lookupLogo } from "../data/logoDirectory";
import { toast } from "./Toast";

export default function CompanyRegModal({ isOpen, onClose, onResolved, phone, required = false }) {
  const [regNo, setRegNo] = useState("");
  // What the registry said: idle | verified | notfound | error. Kept apart from `busy`
  // below — folding the two together meant "saving" erased the lookup result, and with it
  // the verified name the save was about to send.
  const [lookup, setLookup] = useState("idle");
  // null | "checking" | "saving"
  const [busy, setBusy] = useState(null);
  const [companyName, setCompanyName] = useState("");
  // Whatever the save actually failed with. Kept apart from the lookup states: a
  // registry that didn't answer and a server that refused to attach the company are
  // different problems, and showing the lookup's message for both hid the real one.
  const [saveError, setSaveError] = useState(null);
  // A logo the directory has for this name, and whether the employer wants it. Offered
  // rather than applied: the match is on name alone, and two unrelated businesses do
  // share one — so the wrong mark is a real outcome, and it is theirs to reject.
  const [logo, setLogo] = useState(null);
  const [useLogo, setUseLogo] = useState(false);
  // What saving would do, asked of the server once the registry has named the company:
  // CREATE | JOIN | ALREADY_MEMBER | TAKEN. Joining someone else's registration is never
  // silent — the employer sees it before saving, and TAKEN cannot be saved at all.
  const [preview, setPreview] = useState(null);

  // Back to an empty form, for the next time the dialog opens.
  const reset = () => {
    setRegNo("");
    setLookup("idle");
    setBusy(null);
    setCompanyName("");
    setSaveError(null);
    setLogo(null);
    setUseLogo(false);
    setPreview(null);
  };

  // The person backed out. Only that — a finished registration never calls onClose,
  // because every caller reads onClose as "they did not register" and undoes the role
  // switch that brought them here (Profile, PostJob). Calling it after onResolved too
  // demoted people the moment they had registered, and closed the posting wizard on them.
  const close = () => {
    reset();
    onClose?.();
  };

  const verify = async () => {
    if (regNo.trim().length < 7 || busy) return;
    setBusy("checking");
    setCompanyName("");
    setSaveError(null);
    setLogo(null);
    setUseLogo(false);
    setPreview(null);

    const res = await lookupCompanyByRegNo(regNo);
    setBusy(null);
    if (res.found) {
      setCompanyName(res.name);
      setLookup("verified");
      // Not awaited: the name is confirmed either way, and a logo the directory may not
      // have should never hold up the step that already succeeded.
      lookupLogo(res.name).then(setLogo);
      // Not awaited either: a preview that fails leaves the save to the server, which
      // decides the same way and says so.
      previewCompanyRegistration({ regNo: regNo.trim(), companyName: res.name }).then((p) => {
        if (p?.success && p.data?.outcome) setPreview(p.data);
      });
      return;
    }
    // The registry is the gate, not a convenience. It used to fall through to a typed
    // company name whenever the lookup came back empty, which meant any seven digits and
    // any name at all created a company — the number was never really checked. Both
    // outcomes now stop here; the wording differs so a mistyped number can be told apart
    // from a registry that is simply down.
    setLookup(res.error === "unreachable" ? "error" : "notfound");
  };

  const save = async () => {
    const name = companyName.trim();
    if (!name || busy) return;

    setBusy("saving");
    setSaveError(null);

    const res = await resolveCompany({
      regNo: regNo.trim(),
      companyName: name,
      phone,
      // Only when it was actually ticked. The server re-checks that the URL is one of
      // its own directory's before writing it — see DiilEmployerCompanyService.
      logoUrl: useLogo && logo ? logo : undefined,
    });
    if (!res?.success) {
      // Say what actually went wrong instead of reading as a failed lookup — the number
      // was fine and the registry answered; attaching the company is what did not.
      // Status 0 means the request never got a reply, which is the employer's network
      // rather than anything they typed, and telling them so saves a pointless retry.
      const message =
        res?.status === 0
          ? "Сервертэй холбогдож чадсангүй. Интернэтээ шалгаад дахин оролдоно уу."
          : res?.error || "Бүртгэхэд алдаа гарлаа. Дахин оролдоно уу.";
      setSaveError(res?.status ? `${message} (${res.status})` : message);
      toast.error(message);
      setBusy(null);
      return;
    }

    // Say which of the three it was: joining a company somebody else registered must not
    // look like creating one.
    const outcome = res.data?.outcome;
    if (outcome === "JOINED") {
      toast.warning("Энэ байгууллага Diil-д өмнө бүртгэгдсэн байсан. Таныг менежерээр нэгтгэлээ.");
    } else if (outcome === "ALREADY_MEMBER") {
      toast.info("Та энэ байгууллагад аль хэдийн менежерээр бүртгэлтэй байна.");
    } else {
      toast.success("Байгууллага бүртгэгдлээ. Та менежер эрхтэй боллоо.");
    }

    // The screen reads companyId off this; if the server answered 200 with something
    // else, the employer lands back on "register your company" with no idea why.
    if (!res.data?.id && !res.data?.companyId) {
      console.error("[CompanyRegModal] resolve succeeded but returned no company id:", res.data);
    }
    // onResolved alone: the caller hides the dialog (or it unmounts once the company is
    // known). See `close` for why onClose is not called here.
    reset();
    onResolved?.(res.data);
  };

  const canVerify = regNo.trim().length >= 7 && !busy;
  // Only a company the registry actually named can be saved. `companyName` is written by
  // verify() and nowhere else, so this is the whole rule.
  const canSave = lookup === "verified" && !!companyName.trim() && !busy && preview?.outcome !== "TAKEN";
  const saveLabel =
    preview?.outcome === "JOIN" ? "Нэгдэх"
      : preview?.outcome === "ALREADY_MEMBER" ? "Үргэлжлүүлэх"
        : "Бүртгүүлэх";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="company-reg-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-end md:items-center justify-center p-0 md:p-4"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={required ? undefined : close} />

          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="relative w-full md:w-[460px] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl px-6 pt-10 pb-7 md:px-9 md:pt-11 md:pb-9"
          >
            {!required && (
              <button
                type="button"
                onClick={close}
                aria-label="Хаах"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <FiX size={18} className="text-slate-600" />
              </button>
            )}

            <h2 className="text-[22px] md:text-[26px] font-bold text-center text-[#020618] leading-snug px-2">
              Байгууллагаа бүртгүүлэх
            </h2>
            <p className="mt-2.5 text-center text-sm md:text-base text-slate-400">
              Байгууллагын нэрийг улсын бүртгэлээс баталгаажуулна.
              {required
                ? " Баталгаажсаны дараа зар нийтлэх боломжтой."
                : " Баталгаажуулалтгүйгээр ажил олгогч болох боломжгүй."}
            </p>

            <div className="mt-7">
              <label className="block text-[12.5px] font-extrabold text-[#020618] mb-2">
                Байгууллагын регистр
              </label>
              <div className="flex items-center gap-2">
                <input
                  value={regNo}
                  onChange={(e) => {
                    setRegNo(e.target.value.replace(/[^\d]/g, ""));
                    setLookup("idle");
                    setCompanyName("");
                    setSaveError(null);
                    setPreview(null);
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="1234567"
                  className="flex-1 min-w-0 rounded-[12px] border border-[#E2E8F0] px-4 py-3 text-[14px] text-[#020618] outline-none focus:border-[#020618]"
                />
                <button
                  type="button"
                  onClick={verify}
                  disabled={!canVerify}
                  className={`flex-none rounded-[12px] px-5 py-3 text-[13px] font-extrabold ${
                    canVerify ? "bg-[#020618] text-white" : "bg-[#F1F5F9] text-[#94A3B8]"
                  }`}
                >
                  {busy === "checking" ? "..." : "Шалгах"}
                </button>
              </div>

              {lookup === "verified" && (
                <div className="mt-3 flex items-center gap-2 rounded-[12px] bg-[#F0FDF4] border border-[#22C55E]/30 px-4 py-3">
                  <FiCheckCircle className="flex-none text-[#22C55E]" />
                  <span className="text-[13.5px] font-bold text-[#020618] truncate">{companyName}</span>
                </div>
              )}

              {preview?.outcome === "JOIN" && (
                <p className="mt-3 rounded-[12px] bg-[#FFFBEB] border border-[#F59E0B]/40 px-4 py-3 text-[12.5px] font-bold text-[#92400E] leading-relaxed">
                  Энэ байгууллага Diil-д өмнө бүртгэгдсэн байна. Үргэлжлүүлбэл та шинээр үүсгэхгүй,
                  одоо байгаа байгууллагад менежерээр нэгдэж, зар нийтлэх болон засах эрхтэй болно.
                </p>
              )}
              {preview?.outcome === "ALREADY_MEMBER" && (
                <p className="mt-3 rounded-[12px] bg-[#EFF6FF] border border-[#3B82F6]/30 px-4 py-3 text-[12.5px] font-bold text-[#1E40AF] leading-relaxed">
                  Та энэ байгууллагад аль хэдийн менежерээр бүртгэлтэй байна.
                </p>
              )}
              {preview?.outcome === "TAKEN" && (
                <p className="mt-3 text-[12.5px] font-bold text-[#EF4444] leading-relaxed">
                  {preview.message || "Энэ нэртэй байгууллага Diil-д өөр хэрэглэгчийн нэр дээр бүртгэлтэй байна."}
                </p>
              )}

              {/* Offered, not applied — see the `logo` state above. Shown only once the
                  directory has actually answered, so nothing flickers into place. */}
              {logo && (
                <label className="mt-3 flex items-center gap-3 rounded-[12px] border border-[#E2E8F0] px-4 py-3 cursor-pointer">
                  <img
                    src={logo}
                    alt=""
                    onError={() => setLogo(null)}
                    className="flex-none w-11 h-11 rounded-[10px] object-contain bg-white border border-[#F1F5F9]"
                  />
                  <span className="flex-1 min-w-0 text-[12.5px] font-bold text-[#020618] leading-snug">
                    Энэ логог компанийн зураг болгон ашиглах уу?
                    <span className="block font-medium text-[#94A3B8] mt-0.5">
                      Дараа нь өөрчлөх боломжтой.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={useLogo}
                    onChange={(e) => setUseLogo(e.target.checked)}
                    className="flex-none w-5 h-5 accent-[#020618]"
                  />
                </label>
              )}

              {/* A dead end on purpose. There is no "type it yourself" any more: the
                  company on an ad is the one the state registry names, or there is no ad. */}
              {lookup === "notfound" && (
                <p className="mt-3 text-[12.5px] font-bold text-[#EF4444] leading-relaxed">
                  Энэ регистрээр байгууллага олдсонгүй. Дугаараа шалгаад дахин оролдоно уу.
                </p>
              )}
              {lookup === "error" && (
                <p className="mt-3 text-[12.5px] font-bold text-[#B7791F] leading-relaxed">
                  Улсын бүртгэлийн систем хариу өгсөнгүй. Түр хүлээгээд дахин оролдоно уу.
                </p>
              )}

              {saveError && (
                <p className="mt-3 text-[12.5px] font-bold text-[#EF4444] leading-relaxed">
                  {saveError}
                </p>
              )}
            </div>

            <div className="mt-7 flex items-center gap-3">
              <button
                type="button"
                onClick={close}
                className="flex-1 h-12 rounded-[99px] border border-slate-200 text-[15px] font-bold text-[#020618] hover:border-[#020618] transition-colors"
              >
                {required ? "Буцах" : "Болих"}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!canSave}
                className={`flex-1 h-12 rounded-[99px] text-[15px] font-bold transition-colors ${
                  canSave
                    ? "bg-[#020618] text-white hover:bg-[#29EAFF] hover:text-[#0F172B]"
                    : "bg-[#E2E8F0] text-[#94A3B8]"
                }`}
              >
                {busy === "saving" ? "..." : saveLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
